"use client";

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from "@gsap/react";
import { Calendar04Icon, CancelCircleIcon, CheckmarkCircle01Icon, GlobeIcon, Loading02Icon, PencilEdit01Icon, UserCircleIcon, Wallet01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import gsap from "gsap";
import { useRef } from "react";
gsap.registerPlugin(useGSAP);
const SIZE = 512;
const STROKE_WIDTH = 10;
const RADIUS = SIZE / 2 - STROKE_WIDTH;
const CX = SIZE / 2;
const CY = SIZE / 2;
const TOTAL_STEPS = 5;
const GAP_DEGREES = 10;
const SECTION_DEGREES = (360 - TOTAL_STEPS * GAP_DEGREES) / TOTAL_STEPS;
const STEP_ICONS = {
  "0": GlobeIcon,
  "1": Wallet01Icon,
  "2": Calendar04Icon,
  "3": PencilEdit01Icon,
  "4": UserCircleIcon,
  submitting: Loading02Icon,
  error: CancelCircleIcon,
  success: CheckmarkCircle01Icon
};
function polarToCartesian(angleDeg: number) {
  const angle = (angleDeg - 90) * Math.PI / 180;
  return {
    x: CX + RADIUS * Math.cos(angle),
    y: CY + RADIUS * Math.sin(angle)
  };
}
function arcPath(startAngle: number, endAngle: number) {
  const start = polarToCartesian(startAngle);
  const end = polarToCartesian(endAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? "1" : "0";
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
}
interface CircularStepProgressProps {
  step: string | number;
}
function CircularStepProgress(t0) {
  const $ = _c(18);
  const {
    step
  } = t0;
  const containerRef = useRef(null);
  const iconRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const arcFillRefs = useRef(t1);
  const arcInitialized = useRef(false);
  const CurrentIcon = STEP_ICONS[`${step}` as keyof typeof STEP_ICONS] || GlobeIcon;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      arcFillRefs.current.forEach(_temp);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useGSAP(t2);
  let t3;
  if ($[2] !== step) {
    t3 = () => {
      if (typeof step === "string") {
        return;
      }
      arcFillRefs.current.forEach((path_0, i) => {
        if (!path_0) {
          return;
        }
        const length = path_0.getTotalLength();
        if (!arcInitialized.current) {
          gsap.set(path_0, {
            strokeDasharray: length,
            strokeDashoffset: length
          });
          return;
        }
        console.log(`Animating arc ${i}: length=${length}, step=${step}`);
        gsap.to(path_0, {
          strokeDashoffset: i < step ? 0 : length,
          autoAlpha: i < step ? 1 : 0,
          duration: 0.5,
          ease: "circ.inOut",
          overwrite: true
        });
      });
      arcInitialized.current = true;
    };
    $[2] = step;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== step) {
    t4 = {
      scope: containerRef,
      dependencies: [step]
    };
    $[4] = step;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useGSAP(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (!iconRef.current) {
        return;
      }
      gsap.fromTo(iconRef.current, {
        autoAlpha: 0,
        scale: 0.5,
        y: 8
      }, {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        duration: 0.4,
        ease: "back.out(1.7)"
      });
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== step) {
    t6 = {
      scope: containerRef,
      dependencies: [step],
      revertOnUpdate: true
    };
    $[7] = step;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useGSAP(t5, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      width: SIZE,
      height: SIZE
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg width={SIZE} height={SIZE} className="absolute inset-0" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">{Array.from({
        length: TOTAL_STEPS
      }).map((_, i_0) => {
        const startAngle = i_0 * (SECTION_DEGREES + GAP_DEGREES) + GAP_DEGREES / 2;
        const endAngle = startAngle + SECTION_DEGREES;
        const path_1 = arcPath(startAngle, endAngle);
        return <g key={i_0}><path d={path_1} fill="none" strokeWidth={STROKE_WIDTH} strokeLinecap="round" className="stroke-muted-foreground/20" /><path ref={el => {
            arcFillRefs.current[i_0] = el;
          }} d={path_1} fill="none" strokeWidth={STROKE_WIDTH} strokeDashoffset={0} strokeLinecap="round" className="stroke-primary" /></g>;
      })}</svg>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== CurrentIcon) {
    t9 = <div ref={iconRef}><HugeiconsIcon icon={CurrentIcon} size={256} className="text-primary" /></div>;
    $[11] = CurrentIcon;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = (step as number) + 1;
  let t11;
  if ($[13] !== t10) {
    t11 = <span className="text-lg font-medium text-muted-foreground tabular-nums">{t10} / {TOTAL_STEPS}</span>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <div ref={containerRef} className="relative hidden shrink-0 items-center justify-center xl:flex" style={t7}>{t8}<div className="z-10 flex flex-col items-center gap-1.5">{t9}{t11}</div></div>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
}
function _temp(path) {
  if (path) {
    gsap.set(path, {
      autoAlpha: 0
    });
  }
}
export { CircularStepProgress };